import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import notificationService from '../../services/notificationService';
import './Navbar.css';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  
  // Notification State
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef(null); // Dropdown dışına tıklamayı algılamak için

  // Dil ayarları
  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
  ];
  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  // Bildirimleri Yükle
  const loadNotifications = async () => {
    if (currentUser) {
      try {
        const notifs = await notificationService.getUserNotifications(currentUser.uid);
        setNotifications(notifs);
        
        // Okunmamış sayısını hesapla
        const unread = notifs.filter(n => !n.read).length;
        setUnreadCount(unread);
      } catch (error) {
        console.error('Bildirim yükleme hatası:', error);
      }
    }
  };

  // Periyodik olarak bildirimleri kontrol et (Basit polling - 30 saniyede bir)
  useEffect(() => {
    if (currentUser) {
      loadNotifications();
      const interval = setInterval(loadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  // Dropdown dışına tıklanırsa kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLangMenuOpen(false);
    localStorage.setItem('language', lng);
  };

  const handleNotificationClick = async (notification) => {
    // Bildirimi okundu işaretle
    if (!notification.read) {
      await notificationService.markAsRead(notification.id);
      loadNotifications(); // Sayıyı güncelle
    }

    // İlgili sayfaya yönlendir
    setShowNotifications(false);
    
    if (notification.type.includes('booking')) {
      navigate('/my-lessons');
    } else if (notification.type === 'message') {
      navigate('/messages');
    }
  };

  const markAllRead = async () => {
    if (currentUser) {
      await notificationService.markAllAsRead(currentUser.uid);
      loadNotifications();
    }
  };

  return (
    <nav className="navbar">
      <div className="container">
        <div className="navbar-content">
          {/* Logo */}
          <Link to="/" className="navbar-logo">
            <span className="logo-icon">📚</span>
            <span className="logo-text">Educopolis</span>
          </Link>

          {/* Desktop Menu */}
          <div className="navbar-menu desktop-menu">
            <Link to="/find-teachers" className="nav-link">
              {t('nav.findTeachers')}
            </Link>
            
            {!currentUser && (
              <Link to="/become-teacher" className="nav-link">
                {t('nav.becomeTeacher')}
              </Link>
            )}
            
            {currentUser && (
              <>
                <Link to="/my-lessons" className="nav-link">
                  {t('nav.myLessons')}
                </Link>
                <Link to="/messages" className="nav-link">
                  {t('nav.messages')}
                </Link>
              </>
            )}
          </div>

          {/* Right Side */}
          <div className="navbar-actions">
            
            {/* NOTIFICATIONS BELL */}
            {currentUser && (
              <div className="notification-wrapper" ref={notifRef}>
                <button 
                  className="notification-btn"
                  onClick={() => setShowNotifications(!showNotifications)}
                >
                  <span className="bell-icon">🔔</span>
                  {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount}</span>
                  )}
                </button>

                {showNotifications && (
                  <div className="notification-dropdown">
                    <div className="notification-header">
                      <h3>Notifications</h3>
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} className="mark-read-btn">
                          Mark all as read
                        </button>
                      )}
                    </div>
                    
                    <div className="notification-list">
                      {notifications.length === 0 ? (
                        <div className="no-notifications">No notifications yet</div>
                      ) : (
                        notifications.map(notif => (
                          <div 
                            key={notif.id} 
                            className={`notification-item ${!notif.read ? 'unread' : ''}`}
                            onClick={() => handleNotificationClick(notif)}
                          >
                            <div className="notif-icon">
                              {notif.type.includes('booking') ? '📅' : '💬'}
                            </div>
                            <div className="notif-content">
                              <p className="notif-title">{notif.title}</p>
                              <p className="notif-message">{notif.message}</p>
                              <span className="notif-time">
                                {new Date(notif.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            {!notif.read && <div className="unread-dot"></div>}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Language Selector */}
            <div className="lang-selector">
              <button 
                className="lang-btn" 
                onClick={() => setLangMenuOpen(!langMenuOpen)}
              >
                <span>{currentLanguage.flag}</span>
                <span className="lang-code">{currentLanguage.code.toUpperCase()}</span>
              </button>
              
              {langMenuOpen && (
                <div className="lang-dropdown">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      className={`lang-option ${i18n.language === lang.code ? 'active' : ''}`}
                      onClick={() => changeLanguage(lang.code)}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* User Menu / Auth Buttons */}
            {currentUser ? (
              <div className="user-menu">
                <Link to="/profile" className="user-avatar">
                  {userProfile?.photoURL ? (
                    <img src={userProfile.photoURL} alt={userProfile?.firstName} />
                  ) : (
                    <div className="avatar-placeholder">
                      {userProfile?.firstName?.charAt(0) || 'U'}
                    </div>
                  )}
                </Link>
                <button onClick={handleLogout} className="btn btn-outline btn-sm logout-btn">
                  Logout
                </button>
              </div>
            ) : (
              <div className="auth-buttons">
                <Link to="/login" className="btn btn-outline btn-sm">
                  {t('common.login')}
                </Link>
                <Link to="/signup" className="btn btn-primary btn-sm">
                  {t('common.signup')}
                </Link>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button 
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span>☰</span>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="mobile-menu">
            <Link to="/find-teachers" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>
              {t('nav.findTeachers')}
            </Link>
            {!currentUser && (
              <Link to="/become-teacher" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>
                {t('nav.becomeTeacher')}
              </Link>
            )}
            {currentUser && (
              <>
                <Link to="/my-lessons" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>
                  {t('nav.myLessons')}
                </Link>
                <Link to="/messages" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>
                  {t('nav.messages')}
                </Link>
                <Link to="/profile" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>
                  {t('nav.profile')}
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;